// ============================================================================
// BOQ — Project zones hook
// ============================================================================
// Lists boq.project_zones for a single project_id (uuid). Builds a tree from
// the self-referencing parent_id column and provides create + delete.
//
// RLS: gated on can_write_project(project_id) for writes; can_read_project
// for reads. Admin/admin-pa/supervisor get through automatically.
//
// Zones can nest arbitrarily deep (resort → villa → floor → room). Codes
// are unique per (project_id, parent_id) — application validates locally
// before insert to surface a friendlier error than 23505.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const ZONE_COLUMNS =
  'id, project_id, parent_id, code, label, zone_type, sort_order, ' +
  'built_up_area, carpet_area, construction_area, created_at, updated_at';

const numOrNull = (v) => (v === '' || v === undefined || v === null ? null : Number(v));

const mapZone = (z) => ({
  id: z.id,
  projectId: z.project_id,
  parentId: z.parent_id,
  code: z.code,
  label: z.label,
  zoneType: z.zone_type,
  sortOrder: z.sort_order,
  builtUpArea: z.built_up_area == null ? null : Number(z.built_up_area),
  carpetArea: z.carpet_area == null ? null : Number(z.carpet_area),
  constructionArea: z.construction_area == null ? null : Number(z.construction_area),
  createdAt: z.created_at,
  updatedAt: z.updated_at,
});

const buildTree = (zones) => {
  const byId = new Map();
  zones.forEach((z) => byId.set(z.id, { ...z, children: [] }));
  const roots = [];
  byId.forEach((z) => {
    if (z.parentId && byId.has(z.parentId)) {
      byId.get(z.parentId).children.push(z);
    } else {
      roots.push(z);
    }
  });
  const sortRec = (arr) => {
    arr.sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
    arr.forEach((z) => sortRec(z.children));
  };
  sortRec(roots);
  return roots;
};

export const useBoqProjectZones = (boqProjectId) => {
  const [zones, setZones] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!boqProjectId) {
      setZones([]);
      setTree([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .schema('boq')
        .from('project_zones')
        .select(ZONE_COLUMNS)
        .eq('project_id', boqProjectId)
        .order('sort_order');
      if (fetchErr) throw fetchErr;
      const mapped = (data || []).map(mapZone);
      setZones(mapped);
      setTree(buildTree(mapped));
    } catch (err) {
      console.error('[useBoqProjectZones] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'zones' }));
    } finally {
      setLoading(false);
    }
  }, [boqProjectId]);

  const createZone = useCallback(async (input) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to create a zone.' };
      }
      const payload = {
        project_id: boqProjectId,
        parent_id: input.parentId || null,
        code: input.code?.trim(),
        label: input.label?.trim(),
        zone_type: input.zoneType?.trim() || null,
        sort_order: input.sortOrder ?? 0,
        built_up_area: numOrNull(input.builtUpArea),
        carpet_area: numOrNull(input.carpetArea),
        construction_area: numOrNull(input.constructionArea),
      };
      const { data, error: insertErr } = await supabase
        .schema('boq')
        .from('project_zones')
        .insert(payload)
        .select(ZONE_COLUMNS)
        .single();
      if (insertErr) throw insertErr;
      const mapped = mapZone(data);
      setZones((prev) => {
        const next = [...prev, mapped];
        setTree(buildTree(next));
        return next;
      });
      return { success: true, zone: mapped };
    } catch (err) {
      console.error('[useBoqProjectZones] create failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'zone' }) };
    }
  }, [boqProjectId]);

  const updateZone = useCallback(async (id, input) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to update a zone.' };
      }
      const payload = {};
      if (input.code !== undefined) payload.code = input.code?.trim();
      if (input.label !== undefined) payload.label = input.label?.trim();
      if (input.zoneType !== undefined) payload.zone_type = input.zoneType?.trim() || null;
      if (input.builtUpArea !== undefined) payload.built_up_area = numOrNull(input.builtUpArea);
      if (input.carpetArea !== undefined) payload.carpet_area = numOrNull(input.carpetArea);
      if (input.constructionArea !== undefined) payload.construction_area = numOrNull(input.constructionArea);
      const { data, error: updErr } = await supabase
        .schema('boq')
        .from('project_zones')
        .update(payload)
        .eq('id', id)
        .select(ZONE_COLUMNS)
        .single();
      if (updErr) throw updErr;
      const mapped = mapZone(data);
      setZones((prev) => {
        const next = prev.map((z) => (z.id === id ? mapped : z));
        setTree(buildTree(next));
        return next;
      });
      return { success: true, zone: mapped };
    } catch (err) {
      console.error('[useBoqProjectZones] update failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'zone' }) };
    }
  }, []);

  const deleteZone = useCallback(async (id) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to delete a zone.' };
      }
      const { error: deleteErr } = await supabase
        .schema('boq')
        .from('project_zones')
        .delete()
        .eq('id', id);
      if (deleteErr) throw deleteErr;
      setZones((prev) => {
        const next = prev.filter((z) => z.id !== id);
        setTree(buildTree(next));
        return next;
      });
      return { success: true };
    } catch (err) {
      console.error('[useBoqProjectZones] delete failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'zone' }) };
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { zones, tree, loading, error, refresh: fetchAll, createZone, updateZone, deleteZone };
};
